import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, MapPin, ArrowRight, Quote } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";

export default function HomeStoriesOfFaith() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonies?limit=6").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  // Auto-rotate every 7 seconds
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;

  const active = items[idx];
  const totalAmens = items.reduce((n, t) => n + (t.amen_count || 0), 0);

  return (
    <section className="border-t border-gold-500/20 py-24 md:py-32" data-testid="home-stories">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <Sparkles size={18} className="text-gold-500" />
              <Overline>Stories of Faith</Overline>
            </div>
            <h2 className="mt-5 font-serif text-4xl md:text-6xl leading-[0.95] text-white max-w-3xl">
              What the Lord is <span className="font-accent italic text-gold-gradient">doing.</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-white/60 max-w-xl">
              {items.length} stories shared · {totalAmens} amen{totalAmens === 1 ? "" : "s"} declared. Read some, share your own.
            </p>
          </div>
          <Link
            to="/stories"
            data-testid="home-stories-cta"
            className="inline-flex items-center gap-2 border border-gold-500/40 px-6 py-3 text-xs uppercase tracking-[0.15em] text-white hover:border-gold-400 hover:text-gold-300 transition-colors duration-300"
          >
            All stories <ArrowRight size={13} />
          </Link>
        </Reveal>

        <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] items-stretch">
          {/* Featured rotating story */}
          <div className="relative border border-gold-500/25 bg-ink-paper/40 p-8 md:p-12 corner-frame overflow-hidden" data-testid="home-story-featured">
            <AnimatePresence mode="wait">
              <motion.article
                key={active.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-gold-400 border border-gold-500/30 px-2.5 py-1">
                    {active.category}
                  </span>
                  {active.is_featured && active.role && (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-ink bg-gold-500 px-2.5 py-1" data-testid="featured-badge">
                      ★ {active.role}
                    </span>
                  )}
                  {active.location && (
                    <span className="inline-flex items-center gap-1 text-xs text-white/50">
                      <MapPin size={11} /> {active.location}
                    </span>
                  )}
                </div>
                <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-tight text-white">
                  {active.title}
                </h3>
                <div className="mt-6 border-l-2 border-gold-500/60 pl-5 md:pl-6">
                  <Quote size={18} className="text-gold-500/50 mb-2" strokeWidth={1.5} />
                  <p className="text-white/85 leading-relaxed md:text-[17px] whitespace-pre-wrap line-clamp-6">
                    {active.message}
                  </p>
                </div>
                <p className="mt-6 text-sm text-gold-300">— {active.name}</p>
              </motion.article>
            </AnimatePresence>

            {/* Rotation dots */}
            {items.length > 1 && (
              <div className="mt-8 pt-6 border-t border-gold-500/15 flex items-center justify-between gap-3">
                <div className="flex gap-2" data-testid="story-dots">
                  {items.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => setIdx(i)}
                      aria-label={`Show story ${i + 1}`}
                      data-testid={`story-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === idx ? "w-10 bg-gold-500" : "w-3 bg-white/30 hover:bg-white/60"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {idx + 1} / {items.length}
                </span>
              </div>
            )}
          </div>

          {/* Side stack — 3 other stories */}
          <div className="grid gap-4" data-testid="home-stories-stack">
            {items.slice(0, 3).map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIdx(items.findIndex((x) => x.id === s.id))}
                data-testid={`home-story-mini-${i}`}
                className={`text-left border p-5 md:p-6 transition-colors duration-300 ${
                  s.id === active.id
                    ? "border-gold-400 bg-gold-500/5"
                    : "border-gold-500/15 bg-ink-paper/30 hover:border-gold-500/40"
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-gold-400">{s.category}</span>
                  <span className="text-[10px] text-white/40">·</span>
                  <span className="text-[10px] text-white/50">{s.amen_count} amen{s.amen_count === 1 ? "" : "s"}</span>
                </div>
                <p className="mt-2 font-serif text-lg text-white leading-tight line-clamp-2">
                  {s.title}
                </p>
                <p className="mt-1 text-xs text-white/50">— {s.name}</p>
              </button>
            ))}
            <Link
              to="/stories"
              data-testid="home-stories-share-cta"
              className="border border-dashed border-gold-500/40 p-5 md:p-6 text-center hover:bg-gold-500/5 transition-colors duration-300"
            >
              <p className="font-serif text-lg text-gold-300">Share your own story →</p>
              <p className="mt-1 text-xs text-white/50">Encourage the family</p>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
